import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { SurveyType } from '../../../../../shared/constants';
import { SURVEY_TYPE_DISPLAY_NAME } from '../../../../../shared/surveys';
import NoDataCard from '../../../../Dashboard/detailPanel/NoDataCard';
import type { CustomerSurveyResponse } from '../../../../../store/customers/customerTypes';
import type {
  SurveyInsightsBlock,
  SurveyInsightsGranularity,
  SurveyInsightsPeriod,
} from '../../../../../store/hubOwner/types';
import InsightsPanel from './InsightsPanel';
import SurveyStatsCards from './SurveyStatsCards';
import type { StatCardsStatus } from './surveyStatCards';
import SurveyTrendChart from './SurveyTrendChart';
import SurveyDistribution from './SurveyDistribution';
import SurveyScoresTable from './SurveyScoresTable';
import {
  buildTrendPoints,
  computeSurveyStats,
  formatPeriodRange,
} from './surveyInsights';

interface SurveyInsightsViewProps {
  surveyType: SurveyType;
  // Window metrics for this type from GET /owners/:id/survey_insights.
  block?: SurveyInsightsBlock | null;
  period?: SurveyInsightsPeriod | null;
  windowLabel: string;
  // Bucket size requested for the window; the API's period wins when present.
  granularity: SurveyInsightsGranularity;
  // Every response of this type inside the window (from
  // GET /customers/:id/satisfaction_survey?days=), for the list and the
  // latest tile.
  rows: readonly CustomerSurveyResponse[];
  // The window fetch is in flight: the window panels show a pending state
  // (never zeros or "no data") while the list stays as it is.
  insightsLoading?: boolean;
  // The rows fetch is in flight: the list and the latest tile show a pending
  // state while the window panels stay as they are.
  historyLoading?: boolean;
  // Failure messages from the two fetches, shown in the affected panels.
  insightsError?: string | null;
  historyError?: string | null;
}

// One dashboard view: stats tiles, the trend line, the score distribution and
// the response list for a single survey type, all over the selected window.
const SurveyInsightsView: React.FC<SurveyInsightsViewProps> = ({
  surveyType,
  block,
  period,
  windowLabel,
  granularity,
  rows,
  insightsLoading = false,
  historyLoading = false,
  insightsError,
  historyError,
}) => {
  const stats = useMemo(() => computeSurveyStats(block, rows), [block, rows]);
  const points = useMemo(() => buildTrendPoints(block?.trend), [block]);
  const name = SURVEY_TYPE_DISPLAY_NAME[surveyType];
  // The survey question is the same on every row of a type.
  const question = block?.title ?? rows.find((r) => r.title)?.title ?? null;
  const hasWindowData = (block?.response_count ?? 0) > 0;
  // 'Last 7 days (Sep 28 – Oct 5, 2026)' once the API has resolved the window.
  const periodRange = formatPeriodRange(period);
  const windowText = periodRange
    ? `${windowLabel} (${periodRange})`
    : windowLabel;

  const statsStatus: StatCardsStatus = insightsError
    ? 'unavailable'
    : insightsLoading
      ? 'loading'
      : 'ready';
  const rowsStatus: StatCardsStatus = historyError
    ? 'unavailable'
    : historyLoading
      ? 'loading'
      : 'ready';

  if (
    !hasWindowData &&
    !rows.length &&
    !insightsLoading &&
    !historyLoading &&
    !insightsError &&
    !historyError
  ) {
    return <NoDataCard />;
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={3}
      data-testid={`survey-insights-${surveyType}`}
    >
      <SurveyStatsCards
        stats={stats}
        surveyType={surveyType}
        windowLabel={windowLabel}
        granularity={period?.granularity ?? granularity}
        status={statsStatus}
        rowsStatus={rowsStatus}
      />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'minmax(0, 2fr) minmax(0, 1fr)',
          },
          gap: 3,
        }}
      >
        <InsightsPanel
          title={`${name} score trend`}
          subtitle={question ? `${question} · ${windowText}` : windowText}
          loading={insightsLoading}
          error={insightsError}
          testId="survey-trend-panel"
        >
          {hasWindowData ? (
            <SurveyTrendChart
              points={points}
              surveyType={surveyType}
              period={period}
              average={stats.average}
            />
          ) : (
            <NoDataCard />
          )}
        </InsightsPanel>
        <InsightsPanel
          title="Score distribution"
          subtitle={windowText}
          loading={insightsLoading}
          error={insightsError}
          testId="survey-distribution-panel"
        >
          <SurveyDistribution
            distribution={block?.distribution ?? []}
            surveyType={surveyType}
          />
        </InsightsPanel>
      </Box>
      <InsightsPanel
        title={`All ${name} responses`}
        subtitle={
          historyLoading
            ? windowText
            : `${rows.length} response${rows.length === 1 ? '' : 's'} · ${windowText}`
        }
        loading={historyLoading}
        error={historyError}
        testId="survey-responses-panel"
      >
        {rows.length ? (
          <SurveyScoresTable rows={rows} surveyType={surveyType} />
        ) : (
          <NoDataCard />
        )}
      </InsightsPanel>
    </Box>
  );
};

export default SurveyInsightsView;
